import { useEffect, useRef, useState, type ChangeEvent } from 'react'

import type { I18nT } from '@runtime/core/i18n/i18n'
import { fetchJSON } from '@runtime/shared/api/fetchJSON'
import type { UserProfile } from '@runtime/features/profile/model/profileTypes'

type UseProfileActionsArgs = {
  open: boolean
  user: UserProfile | null | undefined
  authToken: string | null
  t: I18nT
  setLoginOpen: (open: boolean) => void
  updateProfile: (patch: Partial<UserProfile>) => void
  logout: (options?: { discardPendingResults?: boolean }) => void
}

export function useProfileActions({
  open,
  user,
  authToken,
  t,
  setLoginOpen,
  updateProfile,
  logout,
}: UseProfileActionsArgs) {
  const [exportBusy, setExportBusy] = useState(false)
  const [exportError, setExportError] = useState('')
  const [deleteBusy, setDeleteBusy] = useState(false)
  const [deleteError, setDeleteError] = useState('')
  const [avatarDataUrl, setAvatarDataUrl] = useState<string | null>(
    user?.avatarDataUrl || null,
  )
  const avatarFileInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (!open) return
    setAvatarDataUrl(user?.avatarDataUrl || null)
    setExportBusy(false)
    setExportError('')
    setDeleteBusy(false)
    setDeleteError('')
  }, [open, user])

  function handleAvatarFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files && event.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (loadEvent) => {
      const url = loadEvent.target && loadEvent.target.result
      if (typeof url === 'string') {
        setAvatarDataUrl(url)
      }
    }
    reader.readAsDataURL(file)
  }

  function handleAvatarClick(isAuthed: boolean) {
    if (!isAuthed) return
    avatarFileInputRef.current?.click()
  }

  async function handleExportData() {
    if (!authToken) return
    setExportBusy(true)
    setExportError('')
    try {
      const data = await fetchJSON('/api/me/export', {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      const payload = JSON.stringify(data, null, 2)
      const blob = new Blob([payload], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      const date = new Date().toISOString().slice(0, 10)
      link.href = url
      link.download = `united-square-data-${date}.json`
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    } catch {
      setExportError(t('profile.auth.errors.exportFailed'))
    } finally {
      setExportBusy(false)
    }
  }

  async function handleDeleteAccount() {
    if (!authToken) return
    const confirmed = window.confirm(t('profile.auth.deleteConfirm'))
    if (!confirmed) return
    setDeleteBusy(true)
    setDeleteError('')
    try {
      await fetchJSON('/api/me', {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${authToken}` },
      })
      logout({ discardPendingResults: true })
      setLoginOpen(false)
    } catch {
      setDeleteError(t('profile.auth.errors.deleteFailed'))
    } finally {
      setDeleteBusy(false)
    }
  }

  function handleAvatarSave() {
    if (!user) return
    updateProfile({
      avatarDataUrl: avatarDataUrl || user.avatarDataUrl || null,
    })
    setLoginOpen(false)
  }

  function handleLogout() {
    logout()
    setLoginOpen(false)
  }

  return {
    exportBusy,
    exportError,
    deleteBusy,
    deleteError,
    avatarDataUrl,
    avatarFileInputRef,
    handleAvatarFileChange,
    handleAvatarClick,
    handleAvatarSave,
    handleExportData,
    handleDeleteAccount,
    handleLogout,
  }
}
